import { bindActionCreators } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { trackSoftConversionEvent } from '../../actions/tracking';
import type { RootState } from '../../reducers';
import { getItem } from '../../selectors/item';
import { softConversionEvents } from '../../shared/tracking';
import { Align } from '../../styled-components/components/Align';
import { Stack } from '../../styled-components/components/Stack';

interface Branding {
  id: string;
  description?: string;
  name: string;
  logo?: { file: { url: string } | null } | null;
  website?: string;
}

interface PaymentOption {
  externalId: string;
}

const selectBrandings = (state: RootState) => state.brandings.list;

const selectFilteredBrandings = createSelector([selectBrandings], (brandings) =>
  brandings?.filter((x) => x.category.includes('Credit card')),
);

interface PaymentOptionsProps {
  options: PaymentOption[];
}

const PaymentOptions = ({ options }: PaymentOptionsProps) => {
  const { t } = useTranslation(['item']);
  const dispatch = useDispatch();
  const brandings = useSelector<RootState, Branding[] | undefined>(selectFilteredBrandings);
  const item = useSelector(getItem);
  const track = bindActionCreators(trackSoftConversionEvent, dispatch);

  // Early return if options are empty
  if (!options || options.length === 0) return null;

  // Memoize available payment options to avoid unnecessary recalculations
  const available = useMemo(
    () =>
      options
        .map((option) => brandings?.find((b) => b.id === option.externalId))
        .filter(Boolean) as Branding[],
    [options, brandings],
  );

  // Early return if no available payment options are found
  if (!available || available.length === 0) return null;

  return (
    <>
      {available.map((option) => {
        const { logo, name, description, website } = option;
        const logotype = logo?.file?.url || null;

        return (
          <div className="convert-card-body" key={name}>
            <Stack>
              <div className="font-size-small-rem-md">
                <div className="font-medium m-b-mini">{name}</div>
                <div>{description}</div>
                <div className="m-t-mini">
                  <a
                    onClick={() =>
                      track(item, softConversionEvents.SOFT_CONVERSIONS_PAYMENT_OPTION, {
                        paymentOptionName: name,
                      })
                    }
                    data-am-link=""
                    href={website}
                    title={t('applyForCreditCard', { ns: 'item' })}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    data-wayke-action="carpay-external-link"
                  >
                    {t('applyForCreditCard', { ns: 'item' })}
                    <i className="icon-link-external m-l-half" />
                  </a>
                </div>
              </div>
            </Stack>
            {logotype && (
              <Stack>
                <Align $align="center">
                  <img
                    src={`${logotype}?w=80`}
                    srcSet={`${logotype}?w=160 2x`}
                    alt={name}
                    className="l-block"
                  />
                </Align>
              </Stack>
            )}
          </div>
        );
      })}
    </>
  );
};

export default PaymentOptions;
